import { create } from 'zustand';
import { clientAuctionControllerApi } from '@api/api-config.ts';
import {
  ClientAuctionResponseDTO,
  ClientAuctionDetailDTO,
  ClientAuctionSupplierDTO,
  ClientAuctionProductDTO,
  ClientAuctionPriceDTO,
  PriceType,
} from '@api/generated/search-service';
import { useOrgPortal } from '@hooks/useOrgPortal.ts';

export interface AuctionFilters {
  query?: string;
  validFrom?: string;
  validTo?: string;
  supplierId?: number;
  page: number;
  size: number;
}

interface PaginationInfo {
  page: number;
  size: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

interface ClientAuctionStoreState {
  // Seznam
  loading: boolean;
  error: string | null;
  auctions: ClientAuctionResponseDTO[];
  pagination: PaginationInfo | null;

  // Detail
  detailLoading: boolean;
  detailError: string | null;
  auctionDetail: ClientAuctionDetailDTO | null;

  // Dodavatelé
  suppliers: ClientAuctionSupplierDTO[];
  suppliersLoading: boolean;
  suppliersError: string | null;
  suppliersPagination: PaginationInfo | null;

  // Produkty
  products: ClientAuctionProductDTO[];
  productsLoading: boolean;
  productsError: string | null;
  productsPagination: PaginationInfo | null;

  // Ceny
  prices: ClientAuctionPriceDTO[];
  pricesLoading: boolean;
  pricesError: string | null;
  pricesPagination: PaginationInfo | null;

  // Akce
  fetchAuctions: (filters: AuctionFilters) => Promise<void>;
  fetchAuctionDetail: (auctionId: number) => Promise<void>;
  fetchSuppliers: (auctionId: number, params: { page?: number; size?: number; q?: string }) => Promise<void>;
  fetchProducts: (auctionId: number, params: { page?: number; size?: number; q?: string; supplierId?: number }) => Promise<void>;
  fetchPrices: (
    auctionId: number,
    params: { page?: number; size?: number; supplierId?: number; productQuery?: string; priceType?: PriceType; isGuaranteed?: boolean },
  ) => Promise<void>;
  clearDetail: () => void;
}

export const useClientAuctionStore = create<ClientAuctionStoreState>(set => ({
  loading: false,
  error: null,
  auctions: [],
  pagination: null,

  detailLoading: false,
  detailError: null,
  auctionDetail: null,

  // Dodavatelé
  suppliers: [],
  suppliersLoading: false,
  suppliersError: null,
  suppliersPagination: null,

  // Produkty
  products: [],
  productsLoading: false,
  productsError: null,
  productsPagination: null,

  // Ceny
  prices: [],
  pricesLoading: false,
  pricesError: null,
  pricesPagination: null,

  fetchAuctions: async (filters: AuctionFilters) => {
    try {
      set({ loading: true, error: null });
      const { orgId, portalId } = useOrgPortal.getState();

      const response = await clientAuctionControllerApi.getAuctions({
        organizationId: orgId,
        portalId: portalId,
        query: filters.query,
        validFrom: filters.validFrom,
        validTo: filters.validTo,
        supplierId: filters.supplierId,
        page: filters.page,
        size: filters.size,
      });

      const result = response.data;
      set({
        loading: false,
        error: null,
        auctions: result.auctions || [],
        pagination: {
          page: result.currentPage,
          size: result.pageSize,
          total: result.totalElements,
          totalPages: result.totalPages,
          hasNext: result.currentPage < result.totalPages,
          hasPrev: result.currentPage > 1,
        },
      });
    } catch (error) {
      set({
        loading: false,
        error: error instanceof Error ? error.message : 'Chyba při načítání aukcí',
        auctions: [],
        pagination: null,
      });
    }
  },

  fetchAuctionDetail: async (auctionId: number) => {
    try {
      set({ detailLoading: true, detailError: null });
      const { orgId, portalId } = useOrgPortal.getState();

      const response = await clientAuctionControllerApi.getAuctionDetail({
        auctionId,
        organizationId: orgId,
        portalId: portalId,
      });

      set({
        detailLoading: false,
        detailError: null,
        auctionDetail: response.data,
      });
    } catch (error) {
      set({
        detailLoading: false,
        detailError: error instanceof Error ? error.message : 'Chyba při načítání detailu aukce',
        auctionDetail: null,
      });
    }
  },

  fetchSuppliers: async (auctionId: number, params) => {
    try {
      set({ suppliersLoading: true, suppliersError: null });
      const { orgId, portalId } = useOrgPortal.getState();

      const response = await clientAuctionControllerApi.getAuctionSuppliers({
        auctionId,
        organizationId: orgId,
        portalId: portalId,
        q: params.q,
        page: params.page || 1,
        size: params.size || 20,
      });

      const result = response.data;
      set({
        suppliersLoading: false,
        suppliersError: null,
        suppliers: result.suppliers || [],
        suppliersPagination: {
          page: result.currentPage,
          size: result.pageSize,
          total: result.totalElements,
          totalPages: result.totalPages,
          hasNext: result.currentPage < result.totalPages,
          hasPrev: result.currentPage > 1,
        },
      });
    } catch (error) {
      set({
        suppliersLoading: false,
        suppliersError: error instanceof Error ? error.message : 'Chyba při načítání dodavatelů',
        suppliers: [],
        suppliersPagination: null,
      });
    }
  },

  fetchProducts: async (auctionId: number, params) => {
    try {
      set({ productsLoading: true, productsError: null });
      const { orgId, portalId } = useOrgPortal.getState();

      const response = await clientAuctionControllerApi.getAuctionProducts({
        auctionId,
        organizationId: orgId,
        portalId: portalId,
        q: params.q,
        supplierId: params.supplierId,
        page: params.page || 1,
        size: params.size || 20,
      });

      const result = response.data;
      set({
        productsLoading: false,
        productsError: null,
        products: result.products || [],
        productsPagination: {
          page: result.currentPage,
          size: result.pageSize,
          total: result.totalElements,
          totalPages: result.totalPages,
          hasNext: result.currentPage < result.totalPages,
          hasPrev: result.currentPage > 1,
        },
      });
    } catch (error) {
      set({
        productsLoading: false,
        productsError: error instanceof Error ? error.message : 'Chyba při načítání produktů',
        products: [],
        productsPagination: null,
      });
    }
  },

  fetchPrices: async (auctionId: number, params) => {
    try {
      set({ pricesLoading: true, pricesError: null });
      const { orgId, portalId } = useOrgPortal.getState();

      const response = await clientAuctionControllerApi.getAuctionPrices({
        auctionId,
        organizationId: orgId,
        portalId: portalId,
        supplierId: params.supplierId,
        productQuery: params.productQuery,
        priceType: params.priceType,
        isGuaranteed: params.isGuaranteed,
        page: params.page || 1,
        size: params.size || 20,
      });

      const result = response.data;
      set({
        pricesLoading: false,
        pricesError: null,
        prices: result.prices || [],
        pricesPagination: {
          page: result.currentPage,
          size: result.pageSize,
          total: result.totalElements,
          totalPages: result.totalPages,
          hasNext: result.currentPage < result.totalPages,
          hasPrev: result.currentPage > 1,
        },
      });
    } catch (error) {
      set({
        pricesLoading: false,
        pricesError: error instanceof Error ? error.message : 'Chyba při načítání cen',
        prices: [],
        pricesPagination: null,
      });
    }
  },

  clearDetail: () => {
    set({
      detailLoading: false,
      detailError: null,
      auctionDetail: null,
      suppliers: [],
      suppliersLoading: false,
      suppliersError: null,
      suppliersPagination: null,
      products: [],
      productsLoading: false,
      productsError: null,
      productsPagination: null,
      prices: [],
      pricesLoading: false,
      pricesError: null,
      pricesPagination: null,
    });
  },
}));
